import { apiGet, apiPost, apiPatch, apiDelete } from './client';

export type OnboardingBranch = 'steps' | 'admin_employee' | 'admin_contractor';

export interface OnboardingStepLink {
  label: string;
  url: string;
}

export interface OnboardingStep {
  id: number;
  step_id: string;
  branch: OnboardingBranch;
  position: number;
  active: boolean;
  label: string;
  body_markdown: string;
  links: OnboardingStepLink[];
  flags: string[];
}

/** HR list response: object keyed by branch (all steps incl. inactive) */
export interface OnboardingStepsByBranch {
  steps: OnboardingStep[];
  admin_employee: OnboardingStep[];
  admin_contractor: OnboardingStep[];
}

export interface CreateOnboardingStepInput {
  step_id: string;
  branch: OnboardingBranch;
  label: string;
  body_markdown?: string;
  links?: OnboardingStepLink[];
  flags?: string[];
}

export interface UpdateOnboardingStepInput {
  label?: string;
  body_markdown?: string;
  links?: OnboardingStepLink[];
  flags?: string[];
  active?: boolean;
}

/** HR list: GET /onboarding-steps/all — all steps incl. inactive, grouped by branch */
export async function listOnboardingSteps(): Promise<{ data: OnboardingStepsByBranch }> {
  return apiGet('/onboarding-steps/all');
}

/** Create a new step */
export async function createOnboardingStep(
  input: CreateOnboardingStepInput,
): Promise<{ data: OnboardingStep }> {
  return apiPost('/onboarding-steps', input);
}

/** Update label/body_markdown/links/flags/active for a step */
export async function updateOnboardingStep(
  id: number,
  input: UpdateOnboardingStepInput,
): Promise<{ data: OnboardingStep }> {
  return apiPatch(`/onboarding-steps/${id}`, input);
}

/** Reorder all steps within a branch — sends the full new order as step_ids */
export async function reorderOnboardingSteps(
  branch: OnboardingBranch,
  stepIds: number[],
): Promise<void> {
  return apiPatch('/onboarding-steps/reorder', { branch, step_ids: stepIds });
}

/** Soft-delete a step (sets active=false) */
export async function softDeleteOnboardingStep(id: number): Promise<void> {
  return apiDelete(`/onboarding-steps/${id}`);
}

/** Restore a soft-deleted step (sets active=true) */
export async function restoreOnboardingStep(id: number): Promise<{ data: OnboardingStep }> {
  return apiPost(`/onboarding-steps/${id}/restore`);
}
